import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import {jwtDecode} from 'jwt-decode';
import {User} from "../types/user";


// JWT payload interface - extend this based on your actual JWT structure
interface JWTPayload {
    name?: string;
    email?: string;
    // Add other JWT fields as needed
    exp?: number;
    iat?: number;
}

// Context type definition
interface AuthContextType {
    user: User | null;
    token: string | null;
    getUser: () => User | null;
    setUser: (user: User | null) => void;
    isAuthenticated: () => boolean;
    setToken: (token: string | null) => void;
    logout: () => void;
}

// Create the context with undefined as default
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
    children: ReactNode;
}

/**
 * AuthProvider component that wraps the app and provides authentication state
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({children}) => {
    const [user, setUserState] = useState<User | null>(null);
    const [token, setTokenState] = useState<string | null>(null);

    // Initialize auth state from localStorage on mount
    useEffect(() => {
        const storedToken = localStorage.getItem('jwt');
        if (storedToken) {
            try {
                const decoded = jwtDecode<JWTPayload>(storedToken);

                // Check if token is expired
                if (decoded.exp && decoded.exp * 1000 < Date.now()) {
                    // Token expired, clear it
                    localStorage.removeItem('jwt');
                    setTokenState(null);
                    setUserState(null);
                } else {
                    // Token is valid, extract user data
                    setTokenState(storedToken);
                    if (decoded.name && decoded.email) {
                        setUserState({
                            name: decoded.name,
                            email: decoded.email,
                        });
                    }
                }
            } catch (error) {
                console.error('Error decoding JWT:', error);
                localStorage.removeItem('jwt');
                setTokenState(null);
                setUserState(null);
            }
        }
    }, []);

    /**
     * Get the current user
     */
    const getUser = (): User | null => {
        return user;
    };

    /**
     * Set the current user
     */
    const setUser = (newUser: User | null): void => {
        setUserState(newUser);
    };

    /**
     * Check if user is authenticated
     */
    const isAuthenticated = (): boolean => {
        return token !== null && user !== null;
    };

    /**
     * Set the JWT token, decode it, and extract user data
     */
    const setToken = (newToken: string | null): void => {
        if (newToken) {
            try {
                // Store token in localStorage
                localStorage.setItem('jwt', newToken);
                setTokenState(newToken);

                // Decode the token to extract user data
                const decoded = jwtDecode<JWTPayload>(newToken);

                // Check if token is expired
                if (decoded.exp && decoded.exp * 1000 < Date.now()) {
                    console.warn('Token is already expired');
                    localStorage.removeItem('jwt');
                    setTokenState(null);
                    setUserState(null);
                    return;
                }

                // Extract user data from token
                if (decoded.name && decoded.email) {
                    const userData: User = {
                        name: decoded.name,
                        email: decoded.email,
                    };
                    setUserState(userData);
                } else {
                    console.warn('JWT does not contain required user fields (name, email)');
                }
            } catch (error) {
                console.error('Error decoding JWT:', error);
                localStorage.removeItem('jwt');
                setTokenState(null);
                setUserState(null);
            }
        } else {
            // Clear token and user
            localStorage.removeItem('jwt');
            setTokenState(null);
            setUserState(null);
        }
    };

    /**
     * Logout user - clear token and user data
     */
    const logout = (): void => {
        localStorage.removeItem('jwt');
        setTokenState(null);
        setUserState(null);
    };

    const value: AuthContextType = {
        user,
        token,
        getUser,
        setUser,
        isAuthenticated,
        setToken,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use the AuthContext
 * Throws an error if used outside of AuthProvider
 */
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

